import { useState } from 'react';
import styled from '@emotion/styled';
import Popover from '@mui/material/Popover';
import type { DoctorNeedAddressed } from '@trinity/chat';

interface HcpAttributesPopoverProps {
  data: DoctorNeedAddressed;
  variant?: 'icon' | 'button';
}

export function HcpAttributesPopover({ data, variant = 'icon' }: HcpAttributesPopoverProps): React.JSX.Element {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  return (
    <>
      {variant === 'button' ? (
        <ViewHcpButton onClick={(e) => setAnchorEl(e.currentTarget)}>HCP Attributes</ViewHcpButton>
      ) : (
        <HcpIconButton onClick={(e) => setAnchorEl(e.currentTarget)} title="View HCP Attributes">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="8.5" cy="7" r="4" />
            <line x1="20" y1="8" x2="20" y2="14" />
            <line x1="23" y1="11" x2="17" y2="11" />
          </svg>
        </HcpIconButton>
      )}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { borderRadius: '10px', boxShadow: '0 4px 20px rgba(0,0,0,0.12)' } } }}
      >
        <PopoverContent>
          <PopoverHeader>
            <PopoverTitle>HCP Matched Attributes</PopoverTitle>
          </PopoverHeader>
          <Divider />
          <PopoverBody>
            <Row>
              <Label>Theme</Label>
              <Value>{data.themeLabel}</Value>
            </Row>
            <Row>
              <Label>Domain</Label>
              <Value>{data.domainLabel}</Value>
            </Row>
            <Row>
              <Label>Frame</Label>
              <Value>{data.frame}</Value>
            </Row>
            <Row>
              <Label>Headline</Label>
              <Value>{data.headline}</Value>
            </Row>
            <Row>
              <Label>Respondents</Label>
              <Value>{data.nRespondents}</Value>
            </Row>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </>
  );
}

const HcpIconButton = styled('button')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 32,
  height: 32,
  border: 'none',
  borderRadius: 6,
  background: '#f0f0f0',
  color: '#666',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    background: '#e0e0e0',
    color: '#565add',
  },
});

const ViewHcpButton = styled('button')({
  padding: '4px 12px',
  border: '1px solid #d0d0d0',
  borderRadius: 6,
  background: 'white',
  color: '#555',
  fontSize: 11,
  fontWeight: 500,
  cursor: 'pointer',
  transition: 'all 0.15s ease',
  '&:hover': {
    background: '#f0f0f0',
    color: '#1271c4',
    borderColor: '#1271c4',
  },
});

const PopoverContent = styled('div')({
  width: 320,
});

const PopoverHeader = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '10px 16px',
});

const PopoverTitle = styled('div')({
  fontSize: 14,
  fontWeight: 600,
  color: '#1a1a1a',
});

const Divider = styled('div')({
  height: 1,
  background: '#e8e8e8',
});

const PopoverBody = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  padding: '12px 16px 16px',
});

const Row = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  gap: 12,
});

const Label = styled('span')({
  fontSize: 12,
  color: '#999',
  flexShrink: 0,
});

const Value = styled('span')({
  fontSize: 12,
  color: '#333',
  fontWeight: 500,
  textAlign: 'right',
});
